import { useEffect, useState, type FormEvent } from 'react';
import { BoutonPrincipal, BoutonSecondaire, Carte, MessageErreur, Pastille } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import {
  useActiverSurSite,
  useActiverToutesLesRegles,
  useModifierRegle,
  type CanalNotification,
  type RegleAlerte,
} from '../../lib/useAlertes';
import type { VueParametrage } from '../../lib/useParametrage';
import { CLASSE_BOUTON_ENREGISTRER, CLASSE_CHAMP_NOMBRE, messageErreur } from './commun';

/**
 * Règles d'alerte (CDC §11, TF-435) : la direction seule les règle — activation
 * globale et par antenne, destinataires, canaux, délais. Les pôles les lisent
 * dans « Gestion des alertes » et y traitent leurs alertes.
 */
export function OngletAlertes({ alertes }: { alertes: VueParametrage['alertes'] }): JSX.Element {
  const toast = useToast();
  const activationMasse = useActiverToutesLesRegles();
  const [edition, setEdition] = useState<RegleAlerte | null>(null);

  return (
    <div className="space-y-6">
      <Carte
        titre="Règles d’alerte"
        actions={alertes.sites.map((site) => (
          <BoutonSecondaire
            key={site}
            disabled={activationMasse.isPending}
            onClick={() =>
              activationMasse.mutate(
                { site, actif: true },
                {
                  onSuccess: (reponse) => toast(reponse.message),
                  onError: (refus) => toast(messageErreur(refus, 'Activation refusée.')),
                },
              )
            }
          >
            Tout activer sur {site}
          </BoutonSecondaire>
        ))}
      >
        <p className="px-4 py-3 text-xs text-codialis-gray sm:px-6">
          Une règle coupée, ou coupée sur une antenne, n’y ouvre plus d’alerte ; les écrans gardent les gestes à la
          main. Un délai changé vaut pour les prochains passages du moteur, à 6 h et à « Évaluer maintenant ». Chaque
          réglage est tracé dans l’historique.
        </p>
        {alertes.regles.map((regle) => (
          <FicheRegle
            key={regle.code}
            regle={regle}
            canaux={alertes.canaux}
            onModifierDestinataires={() => setEdition(regle)}
          />
        ))}
      </Carte>

      {edition && (
        <ModaleDestinataires
          regle={edition}
          profils={alertes.profils}
          canaux={alertes.canaux}
          onFermer={() => setEdition(null)}
        />
      )}
    </div>
  );
}

function Bascule({
  actif,
  libelle,
  onBasculer,
  desactive,
}: {
  actif: boolean;
  libelle: string;
  onBasculer: () => void;
  desactive: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onBasculer}
      disabled={desactive}
      aria-pressed={actif}
      aria-label={libelle}
      className="flex min-h-[44px] shrink-0 items-center disabled:opacity-50"
    >
      <span
        className={`flex h-6 w-10 items-center rounded-full px-1 transition ${actif ? 'bg-codialis-teal' : 'bg-gray-300'}`}
      >
        <span
          className="h-4 w-4 rounded-full bg-white transition"
          style={{ transform: `translateX(${actif ? '18px' : '0'})` }}
        />
      </span>
    </button>
  );
}

function FicheRegle({
  regle,
  canaux,
  onModifierDestinataires,
}: {
  regle: RegleAlerte;
  canaux: VueParametrage['alertes']['canaux'];
  onModifierDestinataires: () => void;
}): JSX.Element {
  const toast = useToast();
  const modification = useModifierRegle();
  const activationSite = useActiverSurSite();
  const enCours = modification.isPending || activationSite.isPending;
  const signaler = (refus: unknown): void => toast(messageErreur(refus, 'Réglage refusé.'));

  return (
    <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:px-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1.2fr)]">
      <div className="flex min-w-0 items-start gap-3">
        <Bascule
          actif={regle.actif}
          libelle={`${regle.actif ? 'Couper' : 'Activer'} ${regle.libelle}`}
          desactive={enCours}
          onBasculer={() => modification.mutate({ code: regle.code, actif: !regle.actif }, { onError: signaler })}
        />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-codialis-navy">{regle.libelle}</div>
          <div className="mt-0.5 text-[11px] text-codialis-gray">{regle.declencheur}</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {!regle.actif && <Pastille texte="Coupée" ton="neutral" />}
            {!regle.evalueeAutomatiquement && <Pastille texte="Évaluation à venir" ton="warning" />}
            {regle.ouvertes > 0 && <Pastille texte={`${regle.ouvertes} ouverte(s)`} ton="danger" />}
          </div>
          {regle.reglages.length > 0 && <DelaisRegle regle={regle} />}
        </div>
      </div>

      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">Antennes</div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {regle.sites.map((site) => (
            <button
              key={site.code}
              type="button"
              disabled={enCours}
              aria-pressed={site.actif}
              aria-label={`${site.actif ? 'Couper' : 'Activer'} ${regle.libelle} sur ${site.code}`}
              onClick={() =>
                activationSite.mutate({ code: regle.code, site: site.code, actif: !site.actif }, { onError: signaler })
              }
              className={`min-h-[40px] rounded-lg border px-3 text-xs font-bold transition disabled:opacity-50 ${
                site.actif
                  ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                  : 'border-gray-200 bg-gray-50 text-codialis-gray line-through'
              }`}
            >
              {site.code}
            </button>
          ))}
        </div>
      </div>

      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">Destinataires et canaux</div>
        <div className="mt-1 text-xs text-codialis-navy">
          {regle.destinataires.map((destinataire) => destinataire.libelle).join(' · ') || 'Aucun destinataire'}
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {regle.canaux.map((canal) => (
            <Pastille
              key={canal}
              texte={canaux.find((candidat) => candidat.valeur === canal)?.libelle ?? canal}
              ton="info"
            />
          ))}
        </div>
        <button
          type="button"
          onClick={onModifierDestinataires}
          className="mt-1 inline-flex min-h-[40px] items-center text-xs font-semibold text-codialis-sky hover:underline"
        >
          Modifier
        </button>
      </div>
    </div>
  );
}

/** Les délais d'une règle, enregistrés ensemble : deux paliers se jugent l'un par l'autre. */
function DelaisRegle({ regle }: { regle: RegleAlerte }): JSX.Element {
  const toast = useToast();
  const modification = useModifierRegle();
  const valeursDe = (): Record<string, string> =>
    Object.fromEntries(regle.reglages.map((reglage) => [reglage.cle, String(reglage.valeur)]));
  const [saisies, setSaisies] = useState<Record<string, string>>(valeursDe);
  const [erreur, setErreur] = useState<string | null>(null);
  const empreinte = regle.reglages.map((reglage) => `${reglage.cle}=${reglage.valeur}`).join('&');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- l'empreinte résume les valeurs reçues
  useEffect(() => setSaisies(valeursDe()), [empreinte]);

  const inchange = regle.reglages.every((reglage) => Number(saisies[reglage.cle]) === reglage.valeur);
  const incomplet = regle.reglages.some((reglage) => (saisies[reglage.cle] ?? '').trim() === '');

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    const reglages = Object.fromEntries(regle.reglages.map((reglage) => [reglage.cle, Number(saisies[reglage.cle])]));
    modification.mutate(
      { code: regle.code, reglages },
      {
        onSuccess: () => toast(`${regle.libelle} : délais enregistrés`),
        onError: (refus) => setErreur(messageErreur(refus, 'Les délais n’ont pas pu être enregistrés.')),
      },
    );
  };

  return (
    <form onSubmit={enregistrer} className="mt-3 space-y-2 rounded-xl bg-codialis-sand p-3">
      <div className="flex flex-wrap items-end gap-3">
        {regle.reglages.map((reglage) => {
          const champ = `reglage-${regle.code}-${reglage.cle}`;
          return (
            <div key={reglage.cle} className="min-w-0">
              <label htmlFor={champ} className="block text-[11px] font-semibold text-codialis-navy">
                {reglage.libelle}
              </label>
              <div className="mt-1 flex items-center gap-1.5">
                <input
                  id={champ}
                  type="number"
                  inputMode="numeric"
                  min={reglage.min}
                  max={reglage.max}
                  step={1}
                  required
                  value={saisies[reglage.cle] ?? ''}
                  onChange={(evenement) => setSaisies({ ...saisies, [reglage.cle]: evenement.target.value })}
                  className={`${CLASSE_CHAMP_NOMBRE} bg-white`}
                />
                <span className="text-xs text-codialis-gray">{reglage.uniteLibelle}</span>
              </div>
              <div className="mt-0.5 text-[10px] text-codialis-gray">
                {reglage.min} à {reglage.max} · par défaut {reglage.defaut}
              </div>
            </div>
          );
        })}
        <button
          type="submit"
          disabled={inchange || incomplet || modification.isPending}
          className={CLASSE_BOUTON_ENREGISTRER}
        >
          {modification.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
      {erreur && (
        <p role="alert" className="text-xs font-medium text-red-600">
          {erreur}
        </p>
      )}
    </form>
  );
}

function ModaleDestinataires({
  regle,
  profils,
  canaux,
  onFermer,
}: {
  regle: RegleAlerte;
  profils: VueParametrage['alertes']['profils'];
  canaux: VueParametrage['alertes']['canaux'];
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const modification = useModifierRegle();
  const [destinataires, setDestinataires] = useState<string[]>(regle.destinataires.map((d) => d.profil));
  const [choixCanaux, setChoixCanaux] = useState<CanalNotification[]>(regle.canaux);
  const [erreur, setErreur] = useState<string | null>(null);

  const basculer = <T extends string>(liste: T[], valeur: T): T[] =>
    liste.includes(valeur) ? liste.filter((element) => element !== valeur) : [...liste, valeur];

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    modification.mutate(
      { code: regle.code, destinataires, canaux: choixCanaux },
      {
        onSuccess: () => {
          toast(`${regle.libelle} : destinataires et canaux enregistrés`);
          onFermer();
        },
        onError: (refus) => setErreur(messageErreur(refus, 'Le réglage n’a pas pu être enregistré.')),
      },
    );
  };

  return (
    <Modale titre="Destinataires et canaux" sousTitre={regle.libelle} ouverte onFermer={onFermer}>
      <form onSubmit={enregistrer} className="space-y-5">
        <fieldset>
          <legend className="text-xs font-bold uppercase tracking-wider text-codialis-gray">Profils prévenus</legend>
          <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {profils.map((profil) => (
              <label
                key={profil.valeur}
                className="flex min-h-[44px] items-center gap-2 rounded-lg bg-gray-50 px-3 text-sm text-codialis-navy"
              >
                <input
                  type="checkbox"
                  checked={destinataires.includes(profil.valeur)}
                  onChange={() => setDestinataires(basculer(destinataires, profil.valeur))}
                  className="h-4 w-4 accent-codialis-navy"
                />
                {profil.libelle}
              </label>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-codialis-gray">
            Une règle ne prévient que les profils qui peuvent en lire l’objet : ajouter un profil qui n’a pas l’écran
            concerné ne lui ouvre rien.
          </p>
        </fieldset>
        <fieldset>
          <legend className="text-xs font-bold uppercase tracking-wider text-codialis-gray">Canaux</legend>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {canaux.map((canal) => (
              <label
                key={canal.valeur}
                className="flex min-h-[44px] items-center gap-2 rounded-lg bg-gray-50 px-3 text-sm text-codialis-navy"
              >
                <input
                  type="checkbox"
                  checked={choixCanaux.includes(canal.valeur)}
                  onChange={() => setChoixCanaux(basculer(choixCanaux, canal.valeur))}
                  className="h-4 w-4 accent-codialis-navy"
                />
                {canal.libelle}
              </label>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-codialis-gray">
            Un canal non raccordé — le SMS sans expéditeur déclaré — est tracé sans expédition.
          </p>
        </fieldset>
        {erreur && <MessageErreur message={erreur} />}
        <div className="flex flex-wrap justify-end gap-2">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal
            type="submit"
            disabled={destinataires.length === 0 || choixCanaux.length === 0 || modification.isPending}
          >
            {modification.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}
